import React, { useState, useCallback } from 'react';
import { toast } from '../utils/toast';
import { aiChatService } from '../services/aiChatService';
import { QuickStart } from '../components/QuickStart';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { outputPolishingService } from '../services/outputPolishingService';
import { ContentPolishingLoader } from '../components/ContentPolishingLoader';
import DeploymentOptions from '../components/DeploymentOptions';

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

export function GrantWriter() {
  const [formData, setFormData] = useState({
    organizationName: '',
    projectTitle: '',
    missionStatement: '',
    projectDescription: '',
    targetAudience: '',
    budget: '',
    timeline: '',
    expectedOutcomes: ''
  });
  
  const [generatedProposal, setGeneratedProposal] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [polishingStage, setPolishingStage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  const quickStartGuides = [
    {
      title: "Project Setup",
      description: "Define your organization's key details and project scope to create a compelling foundation for your proposal.",
      keywords: ["Organization", "Project", "Scope"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      title: "AI Generation",
      description: "Let our AI transform your inputs into a professionally structured grant proposal with all essential components.",
      keywords: ["Generate", "Structure", "Polish"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Review & Refine",
      description: "Review your generated proposal, make adjustments, and ensure all components align with grant requirements.",
      keywords: ["Review", "Edit", "Submit"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      )
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleVoiceTranscriptionComplete = (field, transcribedText) => {
    setFormData(prev => ({
      ...prev,
      [field]: transcribedText
    }));
  };

  // Helper function to delay execution
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Helper function to check if the error is a connection error
  const isConnectionError = (error) => {
    return error.message?.toLowerCase().includes('connection') ||
           error.message?.toLowerCase().includes('network') ||
           error.message?.toLowerCase().includes('timeout') ||
           !navigator.onLine;
  };

  // Function to attempt AI service connection
  const tryAIConnection = async () => {
    try {
      // Simple test call to check connection
      await aiChatService.generateResponse('test', [], 'connectionTest');
      return true;
    } catch (error) {
      return false;
    }
  };

  // Reset function for form and states
  const resetStates = () => {
    setIsGenerating(false);
    setPolishingStage(null);
    setRetryCount(0);
    setIsRetrying(false);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    // Check internet connection first
    if (!navigator.onLine) {
      toast.error('No internet connection. Please check your connection and try again.');
      return;
    }

    const requiredFields = ['organizationName', 'projectTitle', 'projectDescription'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());
    
    if (missingFields.length > 0) {
      toast.warning('Please fill in the required fields: ' + 
        missingFields.map(field => field.replace(/([A-Z])/g, ' $1').toLowerCase()).join(', '));
      return;
    }

    // Start generation process
    setIsGenerating(true);
    setPolishingStage('generating');
    
    const generateWithRetry = async (retryAttempt = 0) => {
      try {
        // Check AI service connection
        const isConnected = await tryAIConnection();
        if (!isConnected) {
          throw new Error('Unable to connect to AI service');
        }

        const prompt = `
          Please write a professional grant proposal based on the following information:
          
          Organization Name: ${formData.organizationName}
          Project Title: ${formData.projectTitle}
          Mission Statement: ${formData.missionStatement}
          Project Description: ${formData.projectDescription}
          Target Audience: ${formData.targetAudience}
          Budget: ${formData.budget}
          Timeline: ${formData.timeline}
          Expected Outcomes: ${formData.expectedOutcomes}
          
          Format the proposal with appropriate sections including: Executive Summary, Organization Background, 
          Project Description, Goals and Objectives, Implementation Plan, Budget Justification, Evaluation Plan, 
          and Sustainability Plan. Make it compelling and professional.
        `;
        
        // Generate initial response
        const initialResponse = await aiChatService.generateResponse(
          prompt,
          [],
          'grantWriter'
        );

        // Update stage to polishing
        setPolishingStage('polishing');

        // Polish the response
        const polishedContent = await outputPolishingService.polishOutput(
          initialResponse.content,
          'grantWriter'
        );
        
        setGeneratedProposal(polishedContent);
        toast.success('Proposal generated and polished successfully!');
        resetStates();

      } catch (error) {
        console.error('Error generating grant proposal:', error);
        
        if (retryAttempt < MAX_RETRIES && isConnectionError(error)) {
          setIsRetrying(true);
          setRetryCount(retryAttempt + 1);
          
          // Show retry toast
          toast.info(`Retrying... Attempt ${retryAttempt + 1} of ${MAX_RETRIES}`);
          
          // Wait before retrying
          await delay(RETRY_DELAY);
          return generateWithRetry(retryAttempt + 1);
        }

        // If max retries reached or other error
        toast.error(
          isConnectionError(error)
            ? 'Unable to connect to AI service. Please try again later.'
            : 'Failed to generate proposal. Please try again.'
        );
        resetStates();
      }
    };

    await generateWithRetry();
  };

  const handleCopy = useCallback(() => {
    try {
      navigator.clipboard.writeText(generatedProposal);
      toast.success('Proposal copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy proposal. Please try selecting and copying manually.');
    }
  }, [generatedProposal]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          {/* Modern grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          
          {/* Top and bottom subtle lines */}
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
          
          {/* Glowing orbs */}
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-500/40 via-primary-300/10 to-primary-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-400/40 via-primary-300/10 to-primary-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-800/50 border border-primary-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                <span className="text-sm text-primary-100">AI-Powered Writing</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                Grant Writer
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-primary-200 max-w-xl">
                Create compelling grant proposals with AI assistance
              </p>
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-primary-200">
                  <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Professional Format</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-primary-200">
                  <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Complete Structure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="space-y-8">
        {/* Form Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="p-6">
            <form onSubmit={handleGenerate} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mission Statement
                  </label>
                  <VoiceRecorder 
                    onTranscriptionComplete={(text) => handleVoiceTranscriptionComplete('missionStatement', text)}
                    placeholder="Click to record your mission statement..."
                    className="mb-4"
                  />
                  <textarea
                    name="missionStatement"
                    value={formData.missionStatement}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Record or type your mission statement..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Description *
                  </label>
                  <VoiceRecorder 
                    onTranscriptionComplete={(text) => handleVoiceTranscriptionComplete('projectDescription', text)}
                    placeholder="Click to record your project description..."
                    className="mb-4"
                  />
                  <textarea
                    name="projectDescription"
                    value={formData.projectDescription}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Record or type your project description..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    name="targetAudience"
                    value={formData.targetAudience}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget
                  </label>
                  <input
                    type="text"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="e.g. $50,000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeline
                  </label>
                  <input
                    type="text"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    placeholder="e.g. 12 months"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Outcomes
                  </label>
                  <textarea
                    name="expectedOutcomes"
                    value={formData.expectedOutcomes}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isGenerating}
                className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                  isGenerating
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating Proposal...
                  </div>
                ) : (
                  'Generate Proposal'
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Generated Content Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Generated Proposal</h2>
              {generatedProposal && !isGenerating && (
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </button>
              )}
            </div>

            <div className="bg-gray-50 rounded-xl p-6 min-h-[500px]">
              {isGenerating ? (
                <div className="relative">
                  <ContentPolishingLoader stage={polishingStage} />
                  {isRetrying && (
                    <div className="absolute bottom-4 left-0 right-0 text-center">
                      <p className="text-sm text-gray-600">
                        Retrying connection... Attempt {retryCount} of {MAX_RETRIES}
                      </p>
                    </div>
                  )}
                </div>
              ) : generatedProposal ? (
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap">{generatedProposal}</div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No proposal generated yet</h3>
                  <p className="mt-1 text-sm text-gray-500">Fill out the form and click "Generate Proposal" to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="grant-writer" position="none" />
    </div>
  );
} 